import { computed, defineAsyncComponent, ref } from "vue";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useErrorHandler } from "@/plugins/common/composables/error";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useLessonLockStore } from "@modules/editor/store/lesson-lock-store.ts";
import { useEditorNavigation } from "@modules/editor/composables/editor-navigation";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useLessonLockError } from "@modules/editor/composables/lesson-lock-error.ts";
import { EDITOR_MODE, NAMESPACE, RESOURCE_STATUS } from "@modules/editor/service/editor.constant";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import StudentGroupResourceApi from "@apis/student.group.resource.api";
import AttemptApi from "@/apis/attempt.api";
import LessonEditHistoryApi from "@apis/lesson.edit.open.history.api";
import MoeLessonApi from "@apis/moe.lesson.api.ts";
import AssignmentLessonApi from "@apis/assignment.lesson.api";
import { isEmpty } from "@/utils/var.check";
import { appSessionStorage, getBrowserTabUuid } from "@utils/app.storage";
import lockApi from "@apis/lock.api.ts";
import { ObjectLockModel } from "@plugins/common/types.ts";

export function useEditorWorkflow() {
  const ResourceEditModal = defineAsyncComponent(
    () => import("@modules/editor/components/lesson/ResourceEditModal.vue"),
  );

  const modal = useModal();
  const { passiveApiFailHandler } = useErrorHandler();
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const lessonLockStore = useLessonLockStore();
  const { showLessonLockedModal, showActivityLockedModal } = useLessonLockError();
  const { getRouteByCurrentPageId, getAssignmentViewRouteByCurrentPageId } = useEditorNavigation();
  const { clearDirty } = useDirty(ref(""));

  // Computed
  const getEditingActivityId = computed(
    () => store.getters[`${NAMESPACE.LESSON}/getEditingActivityId`],
  );
  const currentPageId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentPageId`]);
  const lesson = computed(() => store.getters[`${NAMESPACE.LESSON}/getLesson`]);
  const isEditorInViewMode = computed(
    () => store.getters[`${NAMESPACE.EDITOR}/isEditorInViewMode`],
  );
  const isEditorInEditMode = computed(
    () => store.getters[`${NAMESPACE.EDITOR}/isEditorInEditMode`],
  );
  const resourceDomain = computed(() => store.getters[`${NAMESPACE.LESSON}/resourceDomain`]);
  const lessonPermissions = computed(
    () => store.getters[`${NAMESPACE.LESSON}/getLessonPermissions`],
  );
  const resourceWorkflowStatus = computed(
    () => store.getters[`${NAMESPACE.EDITOR}/resourceWorkflowStatus`],
  );

  const lockService = computed(() => lessonLockStore.getLockService);

  const isStudentGroupResource = computed(() => resourceDomain.value === "class-group");

  const isMOEResource = computed(() => resourceDomain.value === "moe-library");

  const isTemplateResource = computed(() => resourceDomain.value === "alp-template");

  const isAssignmentResource = computed(() => resourceDomain.value === "assignment");

  const canResourceDomainEditSpecificActivity = computed(() => {
    return isStudentGroupResource.value || isMOEResource.value;
  });

  const hasEditLessonPermission = computed(() => {
    return lessonPermissions.value?.hasEditPermission ?? false;
  });

  const showEditLessonButton = computed(() => {
    return hasEditLessonPermission.value;
  });

  const showEditSpecificActivityButton = computed(() => {
    if (!isEditorInViewMode.value) {
      return false;
    }

    if (!canResourceDomainEditSpecificActivity.value) {
      return false;
    }

    return hasEditLessonPermission.value;
  });

  // check if user is editing specific activity
  const isEditingSpecificActivity = computed(() => {
    if (!isEditorInEditMode.value) {
      return false;
    }

    if (!canResourceDomainEditSpecificActivity.value) {
      return false;
    }

    return !isEmpty(getEditingActivityId.value);
  });

  // Methods
  const setEditingActivityId = (payload: string | null) =>
    store.dispatch(`${NAMESPACE.LESSON}/setEditingActivityId`, payload);
  const resetEditingActivityId = () => store.dispatch(`${NAMESPACE.LESSON}/resetEditingActivityId`);
  const resetEditingComponentId = () =>
    store.dispatch(`${NAMESPACE.LESSON}/resetEditingComponentId`);
  const resetEditingResourceUuid = () =>
    store.dispatch(`${NAMESPACE.COMPONENT}/resetEditingResourceUuid`);
  const resetEditingContainerComponentId = () =>
    store.dispatch(`${NAMESPACE.LESSON}/resetEditingContainerComponentId`);

  const handleEditSpecificActivity = async () => {
    if (!showEditSpecificActivityButton.value) {
      return;
    }

    // update store resource type
    lessonLockStore.initLockService({
      lessonUuid: lesson.value.uuid,
      activityId: currentPageId.value,
      failHandler: handleLockRetrievalFail,
    });

    if (isStudentGroupResource.value) {
      await handleEditClassGroupResource();
    } else if (isMOEResource.value) {
      await handleEditMOEResource();
    }

    // if lock retrieval is successful, set editing activity id to store
    startEditingSpecificActivity();
  };

  const setSpecificActivityBeingEdited = async (activityId: string) => {
    await setEditingActivityId(activityId);
    appSessionStorage.setItem("editingSpecificActivityId", activityId);
  };

  const startEditingSpecificActivity = () => {
    setEditingActivityId(currentPageId.value);
    appSessionStorage.setItem("editingSpecificActivityId", currentPageId.value);
  };

  const stopEditingSpecificActivity = () => {
    resetEditingActivityId();
    appSessionStorage.removeItem("editingSpecificActivityId");
  };

  const handleEditClassGroupResource = async () => {
    await deletePreviewAttempts();

    if (lesson.value.paused == true) {
      await handleLockServiceAndOpen();
    } else {
      await handleRemoveAccess();
    }
  };

  const handleLockServiceAndOpen = async () => {
    await lessonLockStore.lockOnce();
    const mode = "edit";
    const route = getRouteByCurrentPageId(mode);
    router.push(route);
    saveLessonEditHistory();
  };

  const handleRemoveAccess = async () => {
    try {
      const { code, errorMap } = await StudentGroupResourceApi.removeStudentAccessResource(
        lesson.value.lessonAssignmentUuid,
      );
      if (code !== "ok") {
        const error = Object.values(errorMap)[0] ?? "";
        throw new Error(error);
      }
      await store.dispatch(`${NAMESPACE.LESSON}/setPaused`, true);
    } catch (error) {
      console.error("Error removing student access:", error);
      passiveApiFailHandler(error);
      return;
    }
    showResourceEditModal();
  };

  const showResourceEditModal = () => {
    modal.show(ResourceEditModal, {
      alsLesson: lesson.value.alsLesson,
      actionPrimary: async () => {
        await handleLockServiceAndOpen();
      },
    });
  };

  const handleEditMOEResource = async () => {
    await deletePreviewAttempts();

    if (resourceWorkflowStatus.value == RESOURCE_STATUS.UNPUBLISHED) {
      const response = await MoeLessonApi.edit({
        uuid: lesson.value?.moeLibraryUuid,
        moeLibraryLessonUuid: lesson.value?.uuid,
      });

      if (!response.data) {
        return;
      }
    }

    await lessonLockStore.lockOnce();
    const mode = "edit";
    await router.push(getRouteByCurrentPageId(mode));
    saveLessonEditHistory();
  };

  const deletePreviewAttempts = async () => {
    // delete preview attempts if exist
    const response = await AttemptApi.fetchHasAttempts(lesson.value.uuid);
    if (response.data) {
      await AttemptApi.deletePreviewAttempts(lesson.value.uuid);
    }
  };

  const saveLessonEditHistory = () => {
    if (isTemplateResource.value) return;
    const assignmentUuid = lesson.value?.lessonAssignmentUuid ?? null;
    LessonEditHistoryApi.saveLessonEditHistory(lesson.value.uuid, assignmentUuid);
  };

  // START lock retrieval fail handler
  const handleLockRetrievalFail = (response, isEditFlow = true) => {
    const failedLockService = lockService.value;
    resetEditingComponentId();
    resetEditingResourceUuid();
    resetEditingContainerComponentId();
    clearDirty();

    showLessonLockedModal(
      response,
      failedLockService?.getResourceLockIdentity().resourceType,
      () =>
        handleTakeOverEditing(
          failedLockService,
          failedLockService?.getResourceLockIdentity().resourceType ===
            LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY
            ? currentPageId.value
            : null,
        ),
      handleSwitchToViewMode,
      canResourceDomainEditSpecificActivity.value,
      isEditFlow,
      canResourceDomainEditSpecificActivity.value,
    );

    // when fail to retrieve activity lock, reset back to lesson lock
    if (
      lockService.value!.getResourceLockIdentity().resourceType ===
      LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY
    ) {
      lessonLockStore.initLockService({
        lessonUuid: lesson.value.uuid,
        activityId: null,
        failHandler: handleLockRetrievalFail,
      });
    }
  };

  const handleTakeOverEditing = async (
    lockServiceToTakeOver = lockService.value,
    activityIdToTakeOver: string | null = null,
  ) => {
    if (!lockServiceToTakeOver) {
      return;
    }

    if (isEmpty(lesson.value)) {
      appSessionStorage.setItem("overrideExistingLock", "true");
      window.location.reload();
      return;
    }

    if (!isEmpty(activityIdToTakeOver)) {
      lessonLockStore.initLockService({
        lessonUuid: lesson.value.uuid,
        activityId: activityIdToTakeOver,
        failHandler: handleLockRetrievalFail,
      });
      lockServiceToTakeOver = lessonLockStore.getLockService;
    }

    try {
      await lockServiceToTakeOver?.lockOnce(true);
    } catch {
      return;
    }

    if (!isEmpty(activityIdToTakeOver)) {
      startEditingSpecificActivity();
    }

    await router.push(getRouteByCurrentPageId(EDITOR_MODE.EDIT.toLowerCase()));
  };

  // Fetch assignment lesson data into the store so the view route can be resolved when the
  // user was locked out on page load (before the editor page fetched the lesson).
  const fetchAssignmentLesson = async () => {
    const response = await AssignmentLessonApi.fetchAssignmentLesson({
      uuid: route.params.lessonUuid,
      assignmentUuid: route.query.assignmentUuid ?? null,
    });
    if (response?.code === "ok") {
      await store.dispatch(`${NAMESPACE.LESSON}/setLesson`, response.data);
    }
  };

  const handleSwitchToViewMode = async () => {
    if (isEditorInViewMode.value && route.params.mode === EDITOR_MODE.VIEW.toLowerCase()) {
      return;
    }

    if (isAssignmentResource.value) {
      if (isEmpty(lesson.value)) {
        await fetchAssignmentLesson();
      }
      if (!isEmpty(lesson.value)) {
        await router.push(getAssignmentViewRouteByCurrentPageId());
        return;
      }
    }

    // if user is locked on page load (before any lesson data fetched), reload page to view mode
    // or else just router push to view mode
    if (!isEmpty(lesson.value)) {
      await router.push(getRouteByCurrentPageId(EDITOR_MODE.VIEW.toLowerCase()));
    } else {
      const routeData = router.resolve({
        ...route,
        params: { ...route.params, mode: EDITOR_MODE.VIEW.toLowerCase() },
      });
      window.location.href = routeData.href;
    }
  };
  // END lock retrieval fail handler

  const handleLocksAfterExitEditMode = (lessonUuid: string) => {
    // unlock current resource
    lessonLockStore.unlockResource();

    // check if user is editing specific activity
    if (!isEmpty(getEditingActivityId.value)) {
      // reset editing activity id in lesson store
      stopEditingSpecificActivity();

      // reinit lock service with lesson resource type
      lessonLockStore.initLockService({
        lessonUuid: lessonUuid!,
        activityId: null,
        failHandler: handleLockRetrievalFail,
      });
    }
  };

  const handleSwitchEditingActivity = async (
    targetPageId: number,
    navigateToActivity: () => Promise<void>,
    navigateToViewMode: () => Promise<void>,
  ): Promise<void> => {
    if (!isEditingSpecificActivity.value) return;

    const targetActivityId = String(targetPageId);

    if (String(getEditingActivityId.value) === targetActivityId) {
      return;
    }

    const transferred = await lessonLockStore.transferActivityLock(
      lesson.value.uuid,
      targetActivityId,
      handleLockRetrievalFail,
    );

    if (transferred === null) {
      return;
    }

    if (!transferred) {
      await showTargetActivityLockedModal(targetActivityId, navigateToViewMode);
      return;
    }

    await setSpecificActivityBeingEdited(targetActivityId);
    await navigateToActivity();
  };

  const showTargetActivityLockedModal = async (
    targetActivityId: string,
    navigateToViewMode: () => Promise<void>,
  ): Promise<void> => {
    const browserTabUuid = getBrowserTabUuid();

    try {
      const response = (await lockApi.checkLockOwner(
        LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY,
        targetActivityId,
        browserTabUuid,
      )) as unknown as ObjectLockModel;

      await showActivityLockedModal(response, navigateToViewMode);
    } catch (error) {
      passiveApiFailHandler(error);
    }
  };

  return {
    showEditLessonButton,
    showEditSpecificActivityButton,
    canResourceDomainEditSpecificActivity,
    isEditingSpecificActivity,
    handleEditSpecificActivity,
    startEditingSpecificActivity,
    handleLockRetrievalFail,
    handleEditClassGroupResource,
    handleEditMOEResource,
    handleLocksAfterExitEditMode,
    handleSwitchToViewMode,
    handleTakeOverEditing,
    handleSwitchEditingActivity,
  };
}
